/**
 * Configuration factory utilities for creating type-safe configurations
 */
import { MemberHubConfig, MemberProfileConfig, MembersLibConfig } from '../models';

/**
 * Creates a type-safe MembersLibConfig with defaults
 * @param config Partial configuration to merge with defaults
 * @returns Complete configuration with all required fields
 */
export function createMembersLibConfig(config: Partial<MembersLibConfig> = {}): MembersLibConfig {
  return {
    chatConfig: {
      enabled: false,
      threadType: 'default',
      allowFileUpload: true,
      maxMessageLength: 5000,
      enableNotifications: false,
      ...config.chatConfig,
    },
    permissionConfig: config.permissionConfig || {
      permissions: [],
      roles: [],
    },
    features: {
      ...config.features,
    },
  };
}

/**
 * Creates a type-safe MemberProfileConfig with defaults
 * @param config Partial configuration to merge with defaults
 * @returns Complete configuration with all required fields
 */
export function createMemberProfileConfig(config: Partial<MemberProfileConfig> = {}): MemberProfileConfig {
  return {
    showChatButton: true,
    showDoctorEdit: false,
    showPartnerIdEdit: false,
    showExternalRefs: true,
    showPackageCoverage: false,
    ...config,
  };
}

/**
 * Creates a type-safe MemberHubConfig with defaults
 * @param config Partial configuration to merge with defaults
 * @returns Complete configuration with all required fields
 */
export function createMemberHubConfig(config: Partial<MemberHubConfig> = {}): MemberHubConfig {
  return {
    showComments: true,
    showHealthCheckScheduler: true,
    showPackageCoverage: false,
    enableFreemiumCheck: true,
    ...config,
  };
}

/**
 * Validates configuration and throws descriptive errors
 * @param config Configuration to validate
 * @param requiredFields Array of required field names
 * @throws Error if validation fails
 */
export function validateConfig<T extends Record<string, any>>(config: T, requiredFields: (keyof T)[]): void {
  const missingFields = requiredFields.filter(field => config[field] === undefined);

  if (missingFields.length > 0) {
    throw new Error(`Missing required configuration fields: ${missingFields.join(', ')}`);
  }
}

/**
 * Type guard to check if a value is a valid configuration
 */
export function isValidConfig<T extends Record<string, any>>(value: unknown, requiredFields: (keyof T)[]): value is T {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const obj = value as Record<string, any>;
  return requiredFields.every(field => field in obj);
}
